import {useEffect, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {
  Breadcrumb,
  Button,
  ButtonGroup,
  Col,
  Dropdown,
  Form,
  InputGroup,
  OverlayTrigger,
  Row,
  Spinner,
  Tooltip,
} from 'react-bootstrap'
import {useDisclourse} from '../../hooks/useDisclourse'
import {useLazyQuery, useMutation} from '@apollo/client'
import {GET_ALL_COMMISSION_REPORTS_LOGS_API} from '../../gql/queries/commissionReportsLogsApiQueries'
import {GET_COMMISSION_REPORT_BY_ID_API} from '../../gql/queries/commissionReportsApiQueries'
import {toast} from 'react-toastify'
import {
  IResCommissionReportsLogsAPI,
  NodeCommissionReportLog,
} from '../../types/commissionReportLogApi'
import {hasPermission, parseId, parsePrice} from '../../helpers'
import moment from 'moment'
import {BadgeStatus} from './components/BadgeStatus'
import {ALL_PERMISSIONS, SIZE_PAGE_DEFAULT} from '../../helpers/consts'
import {PaginationUtils} from '../shared/PaginationUtils'
import {PaginationSection} from '../shared/PaginationSection'
import {useGetUser} from '../../store/userStore'
import {URL} from '../../gql/client'
import { ModalEditCommissionReportLog } from './components/ModalEditCommissionReporLog'
import { ModalAssignAgent } from './components/ModalAssignAgent'
import { CONCILIATE_BY_MEMBER_ID_COMMISSION_REPORT, CONCILIATE_BY_AGENT_COMMISSION_REPORT } from '../../gql/mutations/commissionReportMutations'
import { ModalConfirmation } from '../commissionReports/components/ModalConfirmation'
import {OffcanvasSearchCommissionDetails} from '../commissionReportsDetails/components/OffcanvasSearchCommissionDetails'
import { ModalPreview } from '../policies/components/ModalPreview'
import { GET_ONE_POLICY_API } from '../../gql/queries/policiesApiQueries'
import { IResOnePolicyApi } from '../../types/policiesApi'
import {useTelephony} from '../../hooks/useTelephony'


export const CommissionReportsLogsPage = () => {
  const permissions = useGetUser((s) => s.user?.permissions)
  const telephony = useTelephony()
  const canUseCommissionsFinder =
    permissions && hasPermission(permissions, ALL_PERMISSIONS.VIEW_COMMISSION_FINDER)
  // --- React Router
  const {id} = useParams<{id: string}>()
  const navigate = useNavigate()

  // --- Estado para el ID y nombre del reporte

  // --- GraphQL Hooks
  const [getOneCommissionReport, {data: commissionReportById}] = useLazyQuery(
    GET_COMMISSION_REPORT_BY_ID_API
  )

  const [
    gqlGetCommissionReportsLogs,
    { data: dataAllCommissionReportsLogs, loading, error, refetch: refetchEntries },
  ] = useLazyQuery<IResCommissionReportsLogsAPI>(GET_ALL_COMMISSION_REPORTS_LOGS_API, {
    fetchPolicy: 'no-cache',
  })
  


  // --- Modales y UI
  const [isOpenEditLog, setIsOpenEditLog] = useState(false)
  const [infoEditLog, setInfoEditLog] = useState<NodeCommissionReportLog | null>(null)
  const [isOpenAssignAgent, setIsOpenAssignAgent] = useState(false)
  const [isOpenPolicyPreview, setIsOpenPolicyPreview] = useState(false)
  const [selectedPolicyId, setSelectedPolicyId] = useState<string | null>(null)
  
  const [isOpenConciliateByMemberIdModal, setIsOpenConciliateByMemberIdModal] = useState(false)
  const [conciliateByMemberId, { loading: loadingConciliateByMemberId }] = useMutation(CONCILIATE_BY_MEMBER_ID_COMMISSION_REPORT)
  const [conciliateByAgent, { loading: loadingConciliateByAgent }] = useMutation(CONCILIATE_BY_AGENT_COMMISSION_REPORT)
  const [getPolicy] = useLazyQuery<IResOnePolicyApi>(GET_ONE_POLICY_API)

  const handleConfirmConciliateByMemberId = async () => {
    try {
      const { data } = await conciliateByMemberId({ variables: { id: parseId(report.id) } })
      if (data?.commissionReportConciliateByMemberIdMutation?.success) {
        toast.success(data?.commissionReportConciliateByMemberIdMutation?.message)
      } else {
        toast.error('Failed to conciliate by Member ID')
      }
    } catch (error) {
      console.error(error)
      toast.error('⚠️ Unexpected error conciliating by Member ID')
    } finally {
      setIsOpenConciliateByMemberIdModal(false)
    }
  }

  const handleAssignAgentByPolicy = async (log: NodeCommissionReportLog) => {
    if (!log.policy) return

    try {
      // Parsear el policy ID antes de enviarlo
      const policyId = parseId(log.policy.id).toString()

      // Obtener la información de la póliza para conseguir el agente
      const { data } = await getPolicy({ variables: { id: policyId } })
      const policyAgent = data?.policiesById?.user

      if (!policyAgent) {
        toast.error('Policy has no assigned agent')
        return
      }

      // Llamar a la mutation de conciliar por agente
      const result = await conciliateByAgent({
        variables: {
          logIds: [parseId(log.id)],
          userId: parseId(policyAgent.id)
        }
      })

      if (result.data?.commissionReportConciliateByAgentMutation?.success) {
        toast.success(`Agent assigned: ${policyAgent.firstName} ${policyAgent.lastName}`)
        refetchEntries()
      } else {
        toast.error('Failed to assign agent')
      }
    } catch (error) {
      console.error(error)
      toast.error('Error assigning agent by policy')
    }
  }

  const user = useGetUser((s) => s.user)
  const [loadingExports, setLoadingExport] = useState(false)

  const safeParseId = (value: any): number | null => {
    const id = parseId(value) as number
    return !id || isNaN(id) || id === 0 ? null : id
  }

  const handleDownload = async () => {
    if (!user) return
    const {token} = user
    if (!token) return
    const myHeaders = new Headers()
    myHeaders.append('Authorization', `Bearer ${token}`)

    const requestOptions = {
      method: 'GET',
      headers: myHeaders,
    }

    const rawParams: Record<string, string | null | undefined> = {
      id: safeParseId(report.id)?.toString() ?? null,
    }

    const params = new URLSearchParams()

    Object.entries(rawParams).forEach(([key, value]) => {
      if (value != null && value !== '') {
        params.append(key, value)
      }
    })

    setLoadingExport(true)
    // setTimeout(() => {
    //   setLoadingExport(false)
    // }, 5000)
    try {
      const url = `${URL}/export-commission-logs-excel?${params.toString()}`
      console.log('Export URL:', url)
      // const url = `${URL}/export-policy-excel?${params.toString()}`
      const res = await fetch(url, requestOptions)
      if (!res.ok) throw new Error('Error al exportar')
      const blob = await res.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = `${report.name} - logs - ${moment().format('YY-MM-DD')}.xlsx`
      document.body.appendChild(link)
      setLoadingExport(false)
      link.click()
    } catch (err) {
      setLoadingExport(false)
      toast.error(`ERROR: ${(err as {message: string}).message}`)
    }
  }

  // --- Filtros y paginación
  const [tabFilters] = useState('CommissionReportsLogs')
  // Antes del useState
  type IFilterStatus = 'all' | 'PENDING' | 'APPROVED' | 'NOT_ASSIGNED' | 'CANCELED'
  type IFilterMatchType = 'all' | 'MEMBER_ID' | 'MEMBER_ID_SIMILAR' | 'NAME_MATCH' | 'FAMILY_MEMBER' | 'NON_PAYABLE_POLICY' | 'CANCELED_POLICY' | 'NOT_FOUND_MATCH' | 'FOUND_PREVIOUS_YEAR' | 'OTHER'

  const [filterStatus, setFilterStatus] = useState<IFilterStatus>('all')
  const [filterMatchType, setFilterMatchType] = useState<IFilterMatchType>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [first, setFirst] = useState(SIZE_PAGE_DEFAULT)
  const [isPag] = useState(false)
  const [page, setPage] = useState(1)

  // --- Datos del detalle
  const [dataArr, setDataArr] = useState<
    IResCommissionReportsLogsAPI['allCommissionReportsLogs']['edges']
  >([])
  const [commissionReportsLogsSelected, setCommissionReportsLogsSelected] = useState<NodeCommissionReportLog[]>([])

  const initialLogData = { client: '', status: '', memberId: '' }
  const [detailData, setLogData] = useState(initialLogData)

  // --- Efecto para cargar reporte al inicio
  useEffect(() => {
    const decodedId = id ? String(parseId(id)) : null
    if (decodedId) {
      getOneCommissionReport({variables: {id: decodedId}})
    }
  }, [id, getOneCommissionReport])

  // --- Obtener detalles del reporte con filtros
  const handleFilters = () => {
    const commissionReportId = id ? parseId(id) : null

    gqlGetCommissionReportsLogs({
      variables: {
        first,
        offset: (currentPage - 1) * first,
        commissionReport: commissionReportId,
        client: detailData.client || null,
        status: filterStatus === 'all' ? null : filterStatus,
        matchType: filterMatchType === 'all' ? null : filterMatchType,
        memberId: detailData.memberId || null,
      },
    })
  }


  useEffect(() => {
    handleFilters()
    setCommissionReportsLogsSelected([])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [first, gqlGetCommissionReportsLogs, currentPage, tabFilters])

  useEffect(() => {
    if (!dataAllCommissionReportsLogs) return
    const details = dataAllCommissionReportsLogs.allCommissionReportsLogs
    setDataArr(isPag ? (prev) => [...prev, ...details.edges] : details.edges)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataAllCommissionReportsLogs])

  // --- Manejo de errores
  useEffect(() => {
    if (error) toast.error(`Error: ${error.message}`)
  }, [error])

  // --- Helpers UI

  const allNotFoundLogs = dataAllCommissionReportsLogs?.allCommissionReportsLogs?.edges
    ?.map((e) => e.node)
    ?.filter((node) => node.status === 'NOT_FOUND') || []

  const isAllSelected =
    commissionReportsLogsSelected.length > 0 &&
    commissionReportsLogsSelected.length === allNotFoundLogs.length &&
    commissionReportsLogsSelected.every((selected) =>
      allNotFoundLogs.some((log) => log.id === selected.id)
    )

  const toggleAll = () => {
    if (isAllSelected) {
      setCommissionReportsLogsSelected([])
    } else if (dataAllCommissionReportsLogs?.allCommissionReportsLogs?.edges?.length) {
      const notFoundLogs = dataAllCommissionReportsLogs.allCommissionReportsLogs.edges
        .map((e) => e.node)
        .filter((node) => node.status === 'NOT_FOUND')

      setCommissionReportsLogsSelected(notFoundLogs)
    }
  }

  const toggleOne = (id: string) => {
    const exists = commissionReportsLogsSelected.find(log => log.id === id)

    if (exists) {
      setCommissionReportsLogsSelected(commissionReportsLogsSelected.filter(log => log.id !== id))
    } else {
      const found = dataAllCommissionReportsLogs?.allCommissionReportsLogs?.edges.find(
        e => e.node.id === id
      )?.node

      if (found) {
        setCommissionReportsLogsSelected([...commissionReportsLogsSelected, found])
      }
    }
  }

  const handleClientName = () => {
    handleFilters()
  }

  const updatePage = (n: number) => setCurrentPage(n)

  // --- Paginación
  const totalCommissionReportsLogs =
    dataAllCommissionReportsLogs?.allCommissionReportsLogs?.totalCount ?? 0

  const totalPages = Math.ceil(totalCommissionReportsLogs / first)

  const {start, end} = PaginationUtils.getPaginationRange(
    currentPage,
    first,
    totalCommissionReportsLogs
  )

  const {
    isOpen: isOpenSearchDetails,
    onOpen: onOpenSearchDetails,
    onClose: onCloseSearchDetails,
  } = useDisclourse()

  const floatingToolsStyle: React.CSSProperties = {
    position: 'fixed',
    top: 'calc(50% + 50px)',
    right: 0,
    transform: 'translateY(-50%)',
    zIndex: 1051, // por encima del contenido
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  }

  const floatingTabStyle: React.CSSProperties = {
    borderRadius: '10px 0 0 10px',
    padding: '8px 12px',
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  }

  const handleOpenVicidialWebphone = async () => {
    if (telephony.vicidialSession.activeSession) {
      await telephony.openWebphone('VICIDIAL', {preferAgentSession: true})
      return
    }

    if (telephony.vicidialSession.connected || telephony.vicidialSession.connecting) {
      await telephony.openWebphone('VICIDIAL', {preferAgentSession: true})
      return
    }

    await telephony.connectVicidialSession()
  }



  // --- Validación de carga
  if (!commissionReportById || !commissionReportById.commissionReportById) {
    return (
      <div className='d-flex flex-center' style={{height: '30vh'}}>
        <Spinner animation='border' />
      </div>
    )
  }

  // --- Report listo
  const report = commissionReportById.commissionReportById

  // --- Render principal (return) se mantiene igual ---
  return (
    <div className='policies-page'>
      <header className='header'>
        <Breadcrumb>
          <Breadcrumb.Item className='fs-3' onClick={(e) => navigate(`/commissions/${report.id}/details`)}>
            Back to details
          </Breadcrumb.Item>
          <Breadcrumb.Item className='fs-3'>Conciliation</Breadcrumb.Item>
        </Breadcrumb>
      </header>
      <Row className='commissionReports'>
        <Col className='container'>
          <div className='policies-container'>
            <div className='title-header'>
              <h3>
                Report: {report.name}{' '}
                <br />
                <small>
                  Date:{' '}
                  {report.effectiveMonth && report.effectiveYear
                    ? moment(
                        `${report.effectiveYear}-${report.effectiveMonth}-01`,
                        'YYYY-MM-DD'
                      ).format('MMMM YYYY')
                    : ''}
                </small>
              </h3>

              <div className='d-flex gap-2 align-items-center'>
                <Button
                  disabled={loadingExports}
                  size='sm'
                  onClick={handleDownload}
                >
                  {loadingExports ? (
                    <>
                      <Spinner animation='border' size='sm' />{' '}
                    </>
                  ) : (
                    <i className='bi bi-download'></i>
                  )}
                  Export
                </Button>
                {report.status === 'DRAFT' && (
                  <>
                    <Button
                      size='sm'
                      disabled={loadingConciliateByMemberId}
                      onClick={() => setIsOpenConciliateByMemberIdModal(true)}
                    >
                      {loadingConciliateByMemberId ? (
                        <Spinner animation='border' size='sm' />
                      ) : (
                        <>
                          <i className='bi bi-calculator' /> Conciliate by Member ID
                        </>
                      )}
                    </Button>

                    <Button
                      size='sm'
                      onClick={() => setIsOpenAssignAgent(true)} // Usamos el mismo modal
                      disabled={commissionReportsLogsSelected.length === 0}
                    >
                      <i className='bi bi-person-check' /> Assign Agent
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div className='filters-header'>
              <InputGroup>
                <Form.Control
                  placeholder='Filter by client name or member ID'
                  aria-label="Client's name or member ID with two button addons"
                  onChange={(e) =>
                    setLogData((prev) => ({
                      ...prev,
                      client: e.target.value,
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleClientName()
                    }
                  }}
                  value={detailData.client}
                  disabled={false}
                />
                <Form.Select
                  value={filterStatus}
                  aria-label='Filter by status'
                  onChange={(e) => setFilterStatus(e.target.value as IFilterStatus)}
                  className='ms-2'
                >
                  <option value='all'>Filter by Status</option>
                  <option value='APPROVED'>Approved</option>
                  <option value='APPROVED_MANUALLY'>Approved manually</option>
                  <option value='APPROVED_DISCREPANCY'>Approved discrepancy</option>
                  <option value='APPROVED_MANUALLY_DISCREPANCY'>Approved manually discrepancy</option>
                  <option value='NOT_FOUND'>Not found</option>
                </Form.Select>

                <Form.Select
                  value={filterMatchType}
                  aria-label='Filter by match type'
                  onChange={(e) => setFilterMatchType(e.target.value as IFilterMatchType)}
                  className='ms-2'
                >
                  <option value='all'>Filter by Match Type</option>
                  <option value='MEMBER_ID'>Member ID</option>
                  <option value='MEMBER_ID_SIMILAR'>Member ID (Similar)</option>
                  <option value='NAME_MATCH'>Name Match</option>
                  <option value='FAMILY_MEMBER'>Family Member</option>
                  <option value='NON_PAYABLE_POLICY'>Non-Payable Policy</option>
                  <option value='CANCELED_POLICY'>Canceled Policy</option>
                  <option value='NOT_FOUND_MATCH'>Not Found</option>
                  <option value='FOUND_PREVIOUS_YEAR'>Found in Previous Year</option>
                  <option value='OTHER'>Other</option>
                </Form.Select>

                <Button
                  disabled={false}
                  onClick={() => handleFilters()}
                  variant='outline-success'
                  style={{border: '1px solid #dbdfe9'}}
                  className='ms-2'
                >
                  Search
                </Button>
              </InputGroup>
            </div>
            <div className='results-header'>
              <PaginationSection
                first={first}
                setFirst={(n) => {
                  setFirst(n)
                  setPage(1)
                }}
                currentPage={currentPage}
                totalPages={totalPages}
                start={start}
                end={end}
                totalPolicies={totalCommissionReportsLogs}
                updatePage={updatePage}
                PaginationUtils={PaginationUtils}
              />
              <div className='col-md-3'>
                <div className='results-filter'>
                  <Col lg={12} md={12} sm={12} xs={12}></Col>
                </div>
              </div>
            </div>

            <div className='table-responsive'>
              <table className='table table-striped table-hover table-scrollable' style={{ whiteSpace: 'nowrap' }}>
                <thead>
                  <tr>
                    <th>
                      <input
                        className='form-check-input border border-primary'
                        style={{ width: '20px', height: '20px' }}
                        type='checkbox'
                        checked={isAllSelected}
                        onChange={toggleAll}
                      />

                    </th>
                    <th className='fw-bold'>Client</th>
                    <th className='fw-bold'>Month Paid</th>
                    <th className='fw-bold'>Status</th>
                    <th className='fw-bold'>Rate</th>
                    <th className='fw-bold'>Members</th>
                    <th className='fw-bold'>Subtotal</th>
                    <th className='fw-bold'>Comments</th>
                    <th className='fw-bold'>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {loading || !dataAllCommissionReportsLogs ? (
                    <tr>
                      <td colSpan={8}>
                        <div className='d-flex flex-center' style={{ height: '30vh' }}>
                          <Spinner animation='border' />
                        </div>
                      </td>
                    </tr>
                  ) : dataArr.length === 0 ? (
                    <tr>
                      <td colSpan={8}>
                        <div className='d-flex flex-center' style={{ height: '30vh' }}>
                          No commission report logs currently added
                        </div>
                      </td>
                    </tr>
                  ) : (
                    // Render logs paginados
                    dataArr
                      .slice(first * (page - 1), first * page)
                      .map(({ node }) => (
                        <tr key={node.id}>
                          {node.status === 'NOT_FOUND' ? (
                            <td>
                              <input
                                className='form-check-input border border-primary'
                                style={{ width: '20px', height: '20px' }}
                                type='checkbox'
                                checked={commissionReportsLogsSelected?.some((item) => item.id === node.id)}
                                onChange={(e) => {
                                  e.stopPropagation()
                                  toggleOne(node.id)
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                          ) : (
                            <td />  // o simplemente no renderizas nada si no quieres dejar celda vacía
                          )}

                          {/* Client */}
                          <td style={{ minWidth: '200px', whiteSpace: 'normal' }}>
                            <span
                              className='text-primary'
                              style={{ cursor: 'pointer' }}
                              data-tooltip-id={`myt2${parseId(node.id)}`}
                            >
                              {node.client}
                            </span>
                            <p className='m-0'>Member ID: {node.memberId}</p>
                            <p className='m-0'>State: {node.stateEU?.code}</p>
                          </td>
                          {/* Month Paid */}
                          <td>
                            <p>
                              {node.monthPaid && node.yearPaid
                                ? moment(`${node.yearPaid}-${node.monthPaid}-01`, 'YYYY-MM-DD').format('MMM YYYY')
                                : node.monthPaid && node.commissionReport.effectiveYear
                                ? moment(`${node.commissionReport.effectiveYear}-${node.monthPaid}-01`, 'YYYY-MM-DD').format('MMM YYYY')
                                : ''}
                            </p>
                          </td>
                          <td>
                            <BadgeStatus state={node.status} />
                          </td>

                          {/* Rate, Members, Subtotal */}
                          <td>{parsePrice(node.rate)}</td>
                          <td>{node.applicants}</td>
                          <td>{parsePrice(parseFloat(node.rate) * node.applicants)}</td>
                          <td style={{ maxWidth: '250px', minWidth: '150px', whiteSpace: 'normal' }}>
                            {node.comments}
                          </td>

                          {/* Actions */}
                          <td className='d-flex gap-2 align-items-center' style={{ minWidth: '120px' }}>
                            {report.status === 'DRAFT' && (
                              <Dropdown as={ButtonGroup}>
                                <Button variant='outline-primary' size='sm' style={{ border: '1px solid #3e97ff' }}>
                                  Actions
                                </Button>
                                <Dropdown.Toggle
                                  split
                                  variant='outline-primary'
                                  size='sm'
                                  id='dropdown-custom-2'
                                  style={{ border: '1px solid #3e97ff' }}
                                />
                                <Dropdown.Menu>
                                  <Dropdown.Item onClick={() => { setInfoEditLog(node); setIsOpenEditLog(true); }}>
                                    <i className='bi bi-pencil-square' /> Update
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => {
                                      setCommissionReportsLogsSelected([node])  // ⬅ se pasa directamente el nodo
                                      setIsOpenAssignAgent(true)
                                    }}
                                  >
                                    <i className='bi bi-pencil-fill' /> Reassign
                                  </Dropdown.Item>
                                  {node.policy && (
                                    <>
                                      <Dropdown.Divider />
                                      <Dropdown.Item
                                        onClick={() => {
                                          setSelectedPolicyId(node.policy!.id)
                                          setIsOpenPolicyPreview(true)
                                        }}
                                      >
                                        <i className='bi bi-eye' /> View Policy
                                      </Dropdown.Item>
                                      {node.status === 'NOT_FOUND' && (
                                        <Dropdown.Item
                                          onClick={() => handleAssignAgentByPolicy(node)}
                                          disabled={loadingConciliateByAgent}
                                        >
                                          <i className='bi bi-person-check' /> Assign Agent by Policy
                                        </Dropdown.Item>
                                      )}
                                    </>
                                  )}
                                  <Dropdown.Divider />
                                </Dropdown.Menu>
                              </Dropdown>
                            )}
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>

          </div>
        </Col>
      </Row>
      {canUseCommissionsFinder && (
        <div style={floatingToolsStyle}>
          <OverlayTrigger
            placement='left'
            overlay={<Tooltip id='commissions-finder-tooltip'>Commissions Finder</Tooltip>}
          >
            <Button
              style={floatingTabStyle}
              variant='light'
              title='Commissions Finder'
              onClick={onOpenSearchDetails}
            >
              <i className='bi bi-search fs-4' />
            </Button>
          </OverlayTrigger>
        </div>
      )}

      {canUseCommissionsFinder && (
        <OffcanvasSearchCommissionDetails
          isOpen={isOpenSearchDetails}
          onClose={onCloseSearchDetails}
        />
      )}
  

      {isOpenEditLog && (
        <ModalEditCommissionReportLog
          isOpen={isOpenEditLog}
          onClose={() => {
            setIsOpenEditLog(false)
            
          }}
          infoEdit={infoEditLog}
          refetch={refetchEntries}
        />
      )}

      {isOpenAssignAgent && (
        <ModalAssignAgent
          isOpen={isOpenAssignAgent}
          onClose={() => {
            setIsOpenAssignAgent(false)
            setCommissionReportsLogsSelected([])
          }}
          logsSelected={commissionReportsLogsSelected}
          refetch={refetchEntries}
        />
      )}

      {isOpenPolicyPreview && selectedPolicyId && (
        <ModalPreview
          isOpen={isOpenPolicyPreview}
          onClose={() => {
            setIsOpenPolicyPreview(false)
            setSelectedPolicyId(null)
          }}
          id={selectedPolicyId}
        />
      )}

      {isOpenConciliateByMemberIdModal && (
        <ModalConfirmation
          isOpen={isOpenConciliateByMemberIdModal}
          onClose={() => setIsOpenConciliateByMemberIdModal(false)}
          onConfirm={handleConfirmConciliateByMemberId}
          refetch={refetchEntries}
          title="Conciliate by MemberId"
          confirmText="Yes, Conciliate"
          confirmVariant="primary"
          body={
            <p>
              <strong>Run conciliation by Member ID?</strong><br />
              This will match <strong>NOT FOUND</strong> records using the Member ID and update the report’s details and totals.
            </p>

          }
        />
      )}
    </div>
  )
}
